import { TaskStatus, TriggerKindInfo } from "client/openapi";
import { facetsOf, isTaskKind, kindOf } from "./kinds";
import {
  cardTaskStep,
  clone,
  closedTaskStep,
  defaultFirstPosition,
  entryCards,
  isSubjects,
  Lane,
  newStep,
  RIGHT_AWAY,
  stagesOf,
  withAction,
  toAdmins,
} from "./model";
import {
  columnById,
  columnByKey,
  fieldByRef,
  nameOf,
  ReferenceData,
  roleOf,
} from "./reference";
import {
  resolveChoice,
  RunContext,
  sampleContext,
  tutorsInContext,
} from "./resolve";
import type {
  Approvers,
  AssignTo,
  ColumnCounts,
  Gate,
  MessageStep,
  PersonKey,
  PersonRole,
  Position,
  SendTo,
  Step,
  StepAction,
  TaskStep,
  TimePosition,
  Trigger,
} from "./types";
import {
  APPROVER_ONE,
  columnCountsWords,
  fieldValueWords,
  firstTimeWords,
  gateWords,
  laterWords,
  lowerFirst,
  nameInWords,
  noteWords,
  subjectNames,
  sendToWords,
  subjectsWords,
  suggestName,
  timeWords,
  variableTokens,
} from "./words";

/*
 * The Automations tab's own logic, apart from its screens: a lane's
 * columns (the steps that happen at the same moment, stacked, and its
 * gates), where a new step or gate goes, the smart defaults for a new
 * step, and the few words the tab adds (the editor's estimate, the
 * columns' counts).
 */

/** Add next step's time after a column that isn't a wait. */
const ONE_DAY_LATER: TimePosition = { kind: "time", n: 1, unit: "days" };

/** The roles each Who can approve choice lets approve. */
const APPROVER_ROLES: Record<Approvers, PersonRole[]> = {
  admins: ["admin"],
  tutors: ["tutor"],
  both: ["admin", "tutor"],
};

type Steps = Lane;

// ── Columns ───────────────────────────────────────────────────────────────

/**
 * One column of a lane, left to right after its entry. "time": the steps
 * that happen at the same moment, stacked, sharing one timing; the first
 * counts from the trigger, a later one from when every step in the column
 * before it finished (after a gate, from when it passed). "gate": trigger
 * cards a run waits at until they are true.
 */
export interface LaneColumn {
  key: string;
  /** Its place, left to right (0: the first after the entry). */
  index: number;
  kind: "time" | "gate";
  stage: number;
  /** A time column's timing, which a step stacked here gets. */
  position: TimePosition;
  steps: Step[];
  /** A gate column's gate. */
  gate: Gate | null;
  /** A time column right after a gate: that gate. */
  after: Gate | null;
  /** Right after a gate, in its stage: its own steps, which happen as it passes. */
  own: boolean;
}

/**
 * A lane's columns, left to right: one per stage of its steps (stagesOf),
 * so two in a row stay two even when their timing matches, and each gate
 * in its stage, before its own steps.
 */
export function laneColumns(steps: Step[], gates: Gate[] = []): LaneColumn[] {
  let last = -1;
  const items: { stage: number; gate: Gate | null; steps: Step[] }[] = [];
  stagesOf(steps).forEach((own) => {
    const stage = own[0].stage ?? last + 1;
    last = stage;
    items.push({ stage, gate: null, steps: own });
  });
  gates.forEach((gate) => items.push({ stage: gate.stage, gate, steps: [] }));
  // By stage, a gate before its own steps (the sort keeps the steps' order).
  items.sort(
    (a, b) => a.stage - b.stage || Number(!!b.gate) - Number(!!a.gate)
  );
  return items.map((item, index) => {
    const before = items[index - 1];
    const after = !item.gate && before?.gate ? before.gate : null;
    return {
      key: item.gate ? `gate:${item.gate.id}` : `column:${item.steps[0].id}`,
      index,
      kind: item.gate ? "gate" : "time",
      stage: item.stage,
      position: item.gate ? clone(RIGHT_AWAY) : item.steps[0].position,
      steps: item.steps,
      gate: item.gate,
      after,
      own: !!after && after.stage === item.stage,
    };
  });
}

/** The steps in the order the lane shows them, column by column. */
export function orderSteps(steps: Step[]): Step[] {
  return laneColumns(steps).flatMap((column) => column.steps);
}

/**
 * The time for a new column right after `columns[index]` (Add next
 * step's to start with): Right after a gate, else 1 day later; with no
 * column yet, the trigger's own default (behind two or more trigger
 * cards, right away).
 */
export function timeAfter(
  ref: ReferenceData,
  automation: Lane,
  columns: LaneColumn[],
  index: number
): TimePosition {
  const left = columns[index] ?? null;
  if (!left)
    return entryCards(automation).length > 1
      ? clone(RIGHT_AWAY)
      : defaultFirstPosition(ref, automation.trigger);
  return clone(left.kind === "gate" ? RIGHT_AWAY : ONE_DAY_LATER);
}

/** A stage after every stage the steps and gates have, for a new column. */
export function nextStage(steps: Step[], gates: Gate[] = []) {
  return (
    Math.max(
      -1,
      ...steps.map((s) => s.stage ?? -1),
      ...gates.map((g) => g.stage)
    ) + 1
  );
}

/**
 * The steps with `step` stacked in `column` after its steps, at its stage
 * and timing (Add additional action); with column null, in a new column
 * at the lane's end, at the step's own timing.
 */
export function insertAfter(
  steps: Step[],
  column: LaneColumn | null,
  step: Step,
  gates: Gate[] = []
): Step[] {
  if (!column) return [...steps, { ...step, stage: nextStage(steps, gates) }];
  const stacked: Step = {
    ...step,
    stage: column.stage,
    position: clone(column.position),
  };
  const last = column.steps[column.steps.length - 1];
  const at = steps.findIndex((s) => s.id === last?.id) + 1;
  return at > 0
    ? [...steps.slice(0, at), stacked, ...steps.slice(at)]
    : [...steps, stacked];
}

/**
 * The steps with `step` as the lane's next step: a new column after the
 * last one (the lane without it), at the step's own timing; right after a
 * gate with no steps of its own, one of them. Add next step's When moves
 * it with this.
 */
export function withNext(
  steps: Step[],
  step: Step,
  gates: Gate[] = []
): Step[] {
  const rest = steps.filter((s) => s.id !== step.id);
  const columns = laneColumns(rest, gates);
  const last = columns[columns.length - 1];
  if (last?.kind === "gate" && step.position.n <= 0)
    return [...rest, { ...step, stage: last.stage }];
  return insertAfter(rest, null, step, gates);
}

/**
 * A column's steps at a new time. A gate's own steps happen as it passes,
 * so later than that they become a column of their own right after it,
 * every later stage moving up one.
 */
export function retimed(
  lane: { steps: Step[]; gates: Gate[] },
  column: LaneColumn,
  position: TimePosition
): { steps: Step[]; gates: Gate[] } {
  const ids = new Set(column.steps.map((s) => s.id));
  if (!column.own || position.n <= 0)
    return {
      steps: lane.steps.map((s) =>
        ids.has(s.id) ? { ...s, position: clone(position) } : s
      ),
      gates: lane.gates,
    };
  const at = column.stage;
  const up = (stage: number) => (stage > at ? stage + 1 : stage);
  return {
    steps: lane.steps.map((s) =>
      ids.has(s.id)
        ? { ...s, stage: at + 1, position: clone(position) }
        : s.stage === undefined
        ? s
        : { ...s, stage: up(s.stage) }
    ),
    gates: lane.gates.map((g) => ({ ...g, stage: up(g.stage) })),
  };
}

/** The gates whose cards wait for this step's task. */
export function waitersOf(gates: Gate[], stepId: string) {
  return gates.filter((gate) =>
    gate.cards.some((card) => cardTaskStep(card.trigger) === stepId)
  );
}

// ── Words ─────────────────────────────────────────────────────────────────

/**
 * A column's label, its real timing: the first column's from the trigger
 * ("Right away", "After 3 days", "1 day before"; behind two or
 * more trigger cards, a plain delay after them), a later column's from the
 * column before it ("Right after", "2 days later"), or a gate's words.
 */
export function columnWords(
  ref: ReferenceData,
  automation: Lane,
  column: LaneColumn
) {
  if (column.gate) return gateWords(ref, automation, column.gate);
  const p = column.position;
  if (column.index === 0)
    return entryCards(automation).length > 1
      ? timeWords(null, p)
      : firstTimeWords(ref, automation.trigger, p);
  return laterWords(p);
}

/**
 * Add next step's When in words, as the timing menu says it: the gap
 * after the lane's last column ("Right after", "1 day later"), or with no
 * column yet the first column's time. columns: the lane's, without the
 * new step.
 */
export function whenWords(
  ref: ReferenceData,
  automation: Lane,
  columns: LaneColumn[],
  position: Position
) {
  if (columns.length > 0) return laterWords(position);
  return entryCards(automation).length > 1
    ? timeWords(null, position)
    : firstTimeWords(ref, automation.trigger, position);
}

/**
 * What each column has going now, in words ("2 scheduled, 1 waiting for
 * approval"; empty when it has nothing), from the backend's counts of the
 * column that holds its steps: a gate's, the people waiting at it ("3
 * waiting here"); its own steps', the rest of that column's.
 */
export function columnCountsOf(
  counts: ColumnCounts[],
  columns: LaneColumn[]
): string[] {
  const holding = (ids: string[]) =>
    counts.find((c) => ids.some((id) => c.stepIds.includes(id)));
  return columns.map((column) => {
    if (column.gate) {
      const found = holding([column.gate.id]);
      return found
        ? columnCountsWords({ scheduled: 0, waiting: 0, here: found.here })
        : "";
    }
    const found = holding(column.steps.map((s) => s.id));
    return found ? columnCountsWords({ ...found, here: 0 }) : "";
  });
}

/** The name a saved automation keeps: a name that followed its first step follows it still. */
export function nameAfter(
  ref: ReferenceData,
  saved: { name: string; trigger: Trigger; steps: Step[] },
  next: { trigger: Trigger; steps: Step[] }
) {
  return saved.name === suggestName(ref, saved)
    ? suggestName(ref, next)
    : saved.name;
}

/**
 * A task's name in words, as Activity says it ("Call the student's
 * family"). A name that is only the task's own name reads "Same name as
 * the task".
 */
export function taskTitle(ref: ReferenceData, name: string) {
  const tokens = variableTokens(ref, name);
  if (
    tokens.length === 1 &&
    tokens[0].kind === "variable" &&
    tokens[0].key === "task.name"
  )
    return "Same name as the task";
  return nameInWords(ref, name) || "Untitled task";
}

// ── Smart defaults ────────────────────────────────────────────────────────

/**
 * The CRM column the step's person was last in, before this step: a move
 * before it, a gate's CRM column, or the trigger's own.
 */
function columnBefore(automation: Lane, stepId: string) {
  const columns = laneColumns(automation.steps, automation.gates);
  const at = columns.findIndex((c) => c.steps.some((s) => s.id === stepId));
  const before = columns.slice(0, at < 0 ? columns.length : at + 1).reverse();
  for (const column of before) {
    const gate = column.gate;
    if (gate) {
      const card = [...gate.cards]
        .reverse()
        .find(
          (c) => c.trigger.kind === "column_joined" && c.trigger.options?.column
        );
      if (card) return String(card.trigger.options.column);
      continue;
    }
    const moves = column.steps.filter(
      (s) => s.id !== stepId && s.action === "crm_move" && s.crmMove.columnId
    );
    const move = moves[moves.length - 1];
    if (move?.action === "crm_move")
      return `${move.crmMove.boardId}:${move.crmMove.columnId}`;
  }
  const { trigger } = automation;
  return trigger.kind === "column_joined" ? trigger.options?.column : null;
}

/**
 * The task steps a Close a task step can close: those in the columns
 * before its own, the one right before it first.
 */
export function closableTaskSteps(steps: Step[], stepId: string): TaskStep[] {
  const columns = laneColumns(steps);
  const at = columns.findIndex((c) => c.steps.some((s) => s.id === stepId));
  return columns
    .slice(0, at < 0 ? columns.length : at)
    .flatMap((c) => c.steps)
    .filter((s): s is TaskStep => s.action === "task")
    .reverse();
}

/**
 * A step's defaults, made smarter for where it sits: a move goes to the
 * next column of the board the person was last put in, and Close a task
 * closes the task of the task step right before it.
 */
export function smartStep(
  ref: ReferenceData,
  automation: Steps,
  step: Step
): Step {
  if (step.action === "close_task" && step.closeTask.stepId === null) {
    const nearest = closableTaskSteps(automation.steps, step.id)[0];
    return nearest
      ? { ...step, closeTask: { ...step.closeTask, stepId: nearest.id } }
      : step;
  }
  if (step.action !== "crm_move" || step.crmMove.columnId !== null) return step;
  const found = columnByKey(ref, columnBefore(automation, step.id));
  if (!found || found.board.builtIn) return step;
  const at = found.board.columns.findIndex((c) => c.id === found.column.id);
  const next = found.board.columns
    .slice(at + 1)
    .find((c) => c.role === found.column.role);
  return next
    ? { ...step, crmMove: { boardId: found.board.id, columnId: next.id } }
    : step;
}

/** A new step doing `action` at `position`, with its trigger's defaults. */
export function makeStep(
  ref: ReferenceData,
  automation: Steps,
  action: StepAction,
  position: Position
): Step {
  return newStep(ref, automation.trigger, action, clone(position));
}

/** The same step doing another action (its id, position and Ask first stay), with smart defaults. */
export function switchAction(
  ref: ReferenceData,
  automation: Steps,
  step: Step,
  action: StepAction
): Step {
  const next = withAction(ref, automation.trigger, step, action);
  return smartStep(
    ref,
    {
      ...automation,
      steps: automation.steps.map((s) => (s.id === step.id ? next : s)),
    },
    next
  );
}

/**
 * The first step of a new automation: a task, ready to save, at the kind's
 * own timing ("3 days after" a first meeting, "1 day before" each
 * meeting). After a task event it is the next one of the same name, for
 * that task's assignees. A trigger that reaches a whole audience at once
 * (On a schedule) starts with a message instead, so a save never makes a
 * task for everyone.
 */
export function firstStep(ref: ReferenceData, trigger: Trigger): Step {
  const at = defaultFirstPosition(ref, trigger);
  if (kindOf(ref, trigger.kind)?.about === TriggerKindInfo.about.AUDIENCE)
    return newStep(ref, trigger, "message", at);
  const step = newStep(ref, trigger, "task", at);
  if (step.action === "task" && isTaskKind(trigger.kind))
    return { ...step, task: { ...step.task, name: "{{task.name}}" } };
  return step;
}

/**
 * The step Add next step opens with, at the timing picked: a message
 * right after a gate that waits for a task to be done, otherwise a task.
 */
export function nextStep(
  ref: ReferenceData,
  automation: Steps,
  position: Position,
  after: Gate | null = null
): Step {
  const afterTask =
    !!after &&
    position.n <= 0 &&
    after.cards.some((card) => cardTaskStep(card.trigger) !== null);
  return makeStep(ref, automation, afterTask ? "message" : "task", position);
}

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * When the first step's trigger changes, what is still at the old
 * trigger's default takes the new trigger's default.
 */
export function followTrigger(
  ref: ReferenceData,
  before: Trigger,
  after: Trigger,
  step: Step
): Step {
  const defaultOf = (trigger: Trigger) => {
    const first = firstStep(ref, trigger);
    return first.action === step.action
      ? first
      : newStep(ref, trigger, step.action);
  };
  const was = defaultOf(before);
  const now = defaultOf(after);
  let next: Step = step;
  if (
    next.action === "task" &&
    was.action === "task" &&
    now.action === "task"
  ) {
    const task = { ...next.task };
    if (task.name === was.task.name) task.name = now.task.name;
    if (same(task.assignTo, was.task.assignTo))
      task.assignTo = clone(now.task.assignTo);
    next = { ...next, task };
  }
  if (
    next.action === "message" &&
    was.action === "message" &&
    now.action === "message" &&
    same(next.message.sendTo, was.message.sendTo)
  )
    next = {
      ...next,
      message: { ...next.message, sendTo: now.message.sendTo },
    };
  if (JSON.stringify(next.position) === JSON.stringify(was.position))
    next = { ...next, position: now.position };
  return next;
}

// ── The editor's estimate ─────────────────────────────────────────────────

function unique(keys: PersonKey[]) {
  return Array.from(new Set(keys));
}

/** "Rosa Diaz", "Rosa Diaz and Sam Doe", "Rosa Diaz, Sam Doe, Amy Smith and 4 more". */
function shortNames(ref: ReferenceData, keys: PersonKey[]) {
  const names = keys.map((k) => nameOf(ref, k));
  if (names.length <= 2) return names.join(" and ");
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
  return `${names.slice(0, 3).join(", ")} and ${names.length - 3} more`;
}

function assignees(
  ref: ReferenceData,
  ctx: RunContext,
  assign: AssignTo,
  trigger: Trigger
) {
  const facets = facetsOf(ref, trigger);
  return unique([
    ...assign.choices.flatMap((c) =>
      resolveChoice(ref, ctx, c, assign, facets)
    ),
    ...assign.people.filter((k) => !!ref.personByKey[k]),
  ]);
}

function recipients(ref: ReferenceData, ctx: RunContext, sendTo: SendTo[]) {
  if (toAdmins(sendTo)) return ref.admins;
  const to: PersonKey[] = [];
  const aboutTutor = !!ctx.about && roleOf(ctx.about) === "tutor";
  if (sendTo.includes("students") && ctx.student) to.push(ctx.student);
  if (sendTo.includes("students") || sendTo.includes("parents"))
    to.push(...ctx.parents);
  if (sendTo.includes("tutors"))
    to.push(
      ...(aboutTutor && ctx.about
        ? [ctx.about]
        : tutorsInContext(ref, ctx, null))
    );
  return unique(to.filter((k) => !!ref.personByKey[k]));
}

/**
 * What a step set to Ask first does before it acts, in words that follow
 * "it would": "ask Sam Doe first", "wait for an admin to approve".
 */
function asksWords(ref: ReferenceData, ctx: RunContext | null, step: Step) {
  const approvers = step.askFirst.approvers ?? "admins";
  const starter = ctx?.eventPerson;
  return starter && APPROVER_ROLES[approvers].includes(roleOf(starter))
    ? `ask ${nameOf(ref, starter)} first`
    : `wait for ${APPROVER_ONE[approvers]} to approve`;
}

/**
 * The head of a message step's preview, for an example from the
 * organization's own people: who it goes to ("To Inas Almasry and Ally
 * Green", or the admins by name), what it asks first, and whose details fill
 * it in (person; null with nobody to show, when it shows the template as
 * written). A real send fills it in for the person the run is about, so that
 * is who it is filled in for: "As Maria Angelo would get it" when they get it,
 * else "About Maria Angelo".
 */
export function messagePreviewHead(
  ref: ReferenceData,
  automation: Steps,
  step: MessageStep
) {
  const ctx = sampleContext(ref, automation.trigger)?.ctx ?? null;
  const about = ctx?.about ?? null;
  const asks = step.askFirst.on
    ? `It would ${asksWords(ref, ctx, step)}.`
    : null;
  if (!ctx || !about)
    return {
      person: null,
      to:
        toAdmins(step.message.sendTo) && ref.admins.length
          ? `To ${shortNames(ref, ref.admins)}`
          : `To ${sendToWords(step.message.sendTo)}`,
      as: null,
      asks,
    };
  const to = recipients(ref, ctx, step.message.sendTo);
  const name = nameOf(ref, about);
  return {
    person: about,
    to: `To ${to.length ? shortNames(ref, to) : "nobody, since nobody fits"}`,
    as: to.includes(about) ? `As ${name} would get it` : `About ${name}`,
    asks,
  };
}

/**
 * One sentence about what the step would do for an example from the
 * organization's own people: "If it ran for Lily Chen now, it would make
 * a task for Rosa Diaz and Sam Doe." Null when there is nobody to show or
 * nothing picked yet.
 */
export function estimateWords(
  ref: ReferenceData,
  automation: Steps,
  step: Step
): string | null {
  const sample = sampleContext(ref, automation.trigger);
  if (!sample) return null;
  const { ctx } = sample;
  let what: string;
  switch (step.action) {
    case "task": {
      const assign = step.task.assignTo;
      const people = assignees(ref, ctx, assign, automation.trigger);
      if (!people.length)
        what = `make a task for ${ref.orgName}'s admins, since nobody else fits`;
      else if (people.length > 1 && assign.share === "each")
        what = `make a task each for ${shortNames(ref, people)}`;
      else if (people.length > 1 && assign.share === "turns")
        what = `make a task for one of ${shortNames(
          ref,
          people
        )}, taking turns`;
      else what = `make a task for ${shortNames(ref, people)}`;
      break;
    }
    case "message":
      // Its preview says who it goes to (messagePreviewHead).
      return null;
    case "crm_move": {
      const found = columnById(
        ref,
        step.crmMove.boardId,
        step.crmMove.columnId
      );
      if (!found) return null;
      what = `move them to ${found.column.name}`;
      break;
    }
    case "crm_field": {
      const config = step.crmField;
      if (isSubjects(config)) {
        if (!config.subject_ids.length) return null;
        what = lowerFirst(
          subjectsWords(
            config.mode,
            subjectNames(ref, config.subject_ids),
            ctx.about ? `${nameOf(ref, ctx.about)}'s` : "their"
          )
        );
        break;
      }
      const field = fieldByRef(ref, config.field);
      const value = config.value;
      if (!field || value === null || value === "") return null;
      what = `set ${field.label} to ${fieldValueWords(ref, field, value)}`;
      break;
    }
    case "close_task": {
      const task = closedTaskStep(automation.steps, step.closeTask.stepId);
      if (!task) return null;
      const name = `“${nameInWords(ref, task.task.name) || "Untitled task"}”`;
      what =
        step.closeTask.status === TaskStatus.CANCELLED
          ? `cancel ${name}`
          : `mark ${name} done`;
      break;
    }
    case "assign_tutor": {
      // Whose turn it is can only come from the backend (useStepEstimate).
      const student = ctx.student;
      const config = step.assignTutor;
      // Nothing picked yet: nothing to say.
      if (
        !student ||
        (config.choose === "subject" && config.subject === null) ||
        (config.choose === "specific" && !config.tutors.length)
      )
        return null;
      const pool = unique(
        config.choose === "specific"
          ? config.tutors.filter((k) => !!ref.personByKey[k])
          : config.choose === "subject"
          ? ref.tutors.filter(
              (t) =>
                config.subject !== null &&
                !!ref.subjectsOfTutor[t]?.includes(config.subject)
            )
          : ref.tutors
      );
      const has = pool.filter((t) => ref.tutorsOf[student]?.includes(t));
      const rest = pool.filter((t) => !has.includes(t));
      const skipped = !pool.length
        ? "No tutor fits."
        : (config.share === "turns" ? has.length : !rest.length)
        ? `${nameOf(ref, student)} already has ${shortNames(ref, has)}.`
        : null;
      if (skipped)
        return `${
          ctx.about
            ? `If it ran for ${nameOf(ref, ctx.about)} now`
            : "If it ran now"
        }, it would be skipped: ${skipped}`;
      what =
        config.share === "all" || rest.length === 1
          ? `assign ${shortNames(ref, rest)} to ${nameOf(ref, student)}`
          : `assign one of ${shortNames(ref, rest)} to ${nameOf(
              ref,
              student
            )}, taking turns`;
      break;
    }
    case "add_note": {
      if (!step.note.text.trim()) return null;
      // The sentence's own period ends it, not the note's.
      const note = `“${noteWords(ref, step.note.text, 60).replace(
        /[.!?]+$/,
        ""
      )}”`;
      what = ctx.about
        ? `add a note to ${nameOf(ref, ctx.about)}'s record: ${note}`
        : `add a note: ${note}`;
      break;
    }
  }
  const lead = ctx.about
    ? `If it ran for ${nameOf(ref, ctx.about)} now`
    : "If it ran now";
  if (!step.askFirst.on) return `${lead}, it would ${what}.`;
  return `${lead}, it would ${asksWords(ref, ctx, step)}, then ${what}.`;
}

const APPROVER_CHIP: Record<Approvers, string> = {
  admins: "Ask first: admins",
  tutors: "Ask first: tutors",
  both: "Ask first: admins or tutors",
};

/** A card's Ask first chip: "Ask first: admins". */
export function askChip(step: Step) {
  return APPROVER_CHIP[step.askFirst.approvers ?? "admins"];
}
